import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { allDocPages, docSections } from "./content";
import DocsLayout from "./layout";

export default function IntroductionPage(): ReactNode {
  const introPage = allDocPages.find(p => p.slug === "introduction")!;

  return (
    <DocsLayout page={introPage}>
      <p className="text-gray-700 leading-relaxed">
        CloudQuote is an on-demand financial API platform. It bundles real-time and historical market
        data, premium financial news, semantic search, and an AI-native query layer behind a unified
        REST interface — so a single subscription can power dashboards, mobile apps, trading systems,
        and AI agents.
      </p>

      <h2 className="text-xl font-bold text-gray-900 mt-10 mb-3">What's in the platform</h2>
      <ul className="text-gray-700 leading-relaxed pl-6 list-disc space-y-1.5">
        <li><strong>Market data:</strong> equities, FX, crypto, commodities, indices, fundamentals, and corporate events.</li>
        <li><strong>News:</strong> the NewsRPM service ingests, normalizes, and indexes financial news from 100+ premium publishers.</li>
        <li><strong>Search:</strong> a hybrid vector + full-text endpoint to discover relevant data without knowing exact tickers or headlines.</li>
        <li><strong>AI layer:</strong> an OpenAI-compatible chat endpoint and a Model Context Protocol (MCP) gateway so any LLM can call CloudQuote APIs as tools.</li>
      </ul>

      <h2 className="text-xl font-bold text-gray-900 mt-10 mb-3">How the docs are organized</h2>
      <p className="text-gray-700 leading-relaxed">
        These docs are split into four sections. If you're new, start with{" "}
        <Link to="/docs/quickstart" className="text-blue-600 underline">Quick Start</Link>. If you're integrating
        from a browser, jump to <Link to="/docs/frontend-integration" className="text-blue-600 underline">Front-End Integration</Link>.
        If you're building an AI agent, see <Link to="/docs/agents" className="text-blue-600 underline">Building AI Agents</Link>.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {docSections.map(section => (
          <div key={section.label} className="border border-gray-200 rounded-lg p-5 bg-gray-50">
            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mt-0 mb-3">{section.label}</h3>
            <ul className="m-0 p-0 list-none space-y-2">
              {section.pages.map(p => (
                <li key={p.slug}>
                  <Link to={p.path} className="text-blue-600 hover:text-blue-800 underline text-sm font-medium">
                    {p.title}
                  </Link>
                  <p className="text-gray-600 text-xs m-0 mt-0.5 leading-snug">{p.description}</p>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-bold text-gray-900 mt-12 mb-3">Need a hand?</h2>
      <p className="text-gray-700 leading-relaxed">
        If something here is unclear or you want guidance picking the right endpoint for your use case,{" "}
        <Link to="/contact" className="text-blue-600 underline">contact us</Link> — a real human will get back to you.
      </p>
    </DocsLayout>
  );
}
